import React, { useState } from 'react';
import {
  Layout,
  Text,
  List,
  Button,
  Image,
  Box,
  BlockStack,
  InlineStack,
  CalloutCard,
  Grid,
  SkeletonDisplayText,
} from '@shopify/polaris';
import { ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import { track } from '@/utils/tracker';
import useHasTolstoyApp from '@/hooks/useHasTolstoyApp';
import { Routes, TolstoyService } from '@/features/integrations';
import { useUserStore } from '@/providers/userStoreProvider';
import { SHOPIFY_TOLSTOY_APP } from '@/constants/links';
import { useUserDataStore } from '@/stores/userDataStore';
interface TolstoyIntegrationProps {
  title: string;
  summary: string;
  integrationSteps: React.ReactNode[];
  examples: { src: string; alt: string }[];
  publishEvent: string;
  getTolstoyEvent: string;
  redirectRoute: Routes;
}

const PublishButton = ({
  publishEvent,
  getTolstoyEvent,
  redirectRoute,
}: {
  publishEvent: string;
  getTolstoyEvent: string;
  redirectRoute: Routes;
}) => {
  const account = useUserStore((state) => state.account);
  const { hasApp: hasTolstoyApp, isLoading } = useHasTolstoyApp();

  const handlePublish = () => {
    if (hasTolstoyApp) {
      track(publishEvent);
      const params = new URLSearchParams();
      if (account?.email) {
        params.set('email', account.email);
      }
      TolstoyService.redirectToTolstoy(redirectRoute, params);
    } else {
      track(getTolstoyEvent);
      window.open(SHOPIFY_TOLSTOY_APP, '_blank');
      // TolstoyService.installApp(appKey, appUrl);
    }
  };
  return (
    <Box>
      <Button variant="primary" onClick={handlePublish} size="large" loading={isLoading}>
        {hasTolstoyApp ? 'Go to Tolstoy' : 'Install Tolstoy'}
      </Button>
    </Box>
  );
};
export const TolstoyIntegration: React.FC<TolstoyIntegrationProps> = ({
  title,
  summary,
  integrationSteps,
  examples,
  publishEvent,
  getTolstoyEvent,
  redirectRoute,
}) => {
  const { isLoggedIn } = useUserDataStore();

  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);

  const handlePrevExample = () => {
    setCurrentExampleIndex((prevIndex) => (prevIndex === 0 ? examples.length - 1 : prevIndex - 1));
  };

  const handleNextExample = () => {
    setCurrentExampleIndex((prevIndex) => (prevIndex === examples.length - 1 ? 0 : prevIndex + 1));
  };

  return (
    <>
      <BlockStack gap="200">
        <Text variant="headingXl" as="h1">
          {title}
        </Text>
        <Grid columns={{ xs: 1, sm: 1, md: 6, lg: 6, xl: 6 }}>
          {/* <InlineStack wrap={false} gap="400"> */}
          <Grid.Cell columnSpan={{ xs: 1, sm: 1, md: 4, lg: 4, xl: 4 }}>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                {summary}
              </Text>
              <BlockStack gap="200">
                <Text variant="headingMd" as="h3">
                  How to publish Bunu videos on your site?
                </Text>
                <List type="number">
                  {integrationSteps.map((step, index) => (
                    <List.Item key={index}>{step}</List.Item>
                  ))}
                </List>
                {isLoggedIn ? (
                  <PublishButton
                    publishEvent={publishEvent}
                    getTolstoyEvent={getTolstoyEvent}
                    redirectRoute={redirectRoute}
                  />
                ) : (
                  <SkeletonDisplayText size="medium" />
                )}
              </BlockStack>
            </BlockStack>
          </Grid.Cell>
          <Grid.Cell columnSpan={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
            <BlockStack inlineAlign="center" gap="200">
              <div className="relative h-[200px] w-[200px]">
                <Image
                  source={examples[currentExampleIndex].src}
                  alt={examples[currentExampleIndex].alt}
                  className="h-full w-full object-cover"
                />
              </div>
              <InlineStack gap="200">
                <Button
                  icon={ChevronLeftIcon}
                  onClick={handlePrevExample}
                  accessibilityLabel="Previous example"
                  variant="monochromePlain"
                />
                <Text variant="bodyMd" as="p">
                  {currentExampleIndex + 1} / {examples.length}
                </Text>
                <Button
                  icon={ChevronRightIcon}
                  onClick={handleNextExample}
                  accessibilityLabel="Next example"
                  variant="monochromePlain"
                />
              </InlineStack>
            </BlockStack>
          </Grid.Cell>
          {/* </InlineStack> */}
        </Grid>
      </BlockStack>
    </>
  );
};
